import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Abre URL externa de forma nativa:
 * - iOS/Android (Capacitor) → Safari/Chrome nativo via Browser plugin
 * - Web/desktop → nova aba
 */
export const openExternalLink = async (url) => {
  if (!url) return;
  try {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url, presentationStyle: 'popover' });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (e) {
    console.warn('openExternalLink failed, falling back to window.open:', e?.message);
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) { /* noop */ }
  }
};

// URLs externas (Formstack) — hardcoded conforme configuração da empresa
export const EXTERNAL_LINKS = {
  COMPLETION_CHECKLIST: 'https://renewalbyandersenatlantaga.formstack.com/forms/installation_checklist',
  MEASURE_INFORMATION: 'https://renewalbyandersenatlantaga.formstack.com/forms/field_measure_submission_form',
  LEAD_FORM: 'https://renewalbyandersenatlantaga.formstack.com/forms/lswp_process_clean_copy',
  REORDER_FORM: 'https://renewalbyandersenatlantaga.formstack.com/forms/installation_onsite_reorder',
};

// Pré-preenchimento do Formstack via query string (field<ID>=valor). IDs do "Post Install Checklist" (form 3423548).
const CHECKLIST_FIELDS = {
  workType: 'field95199113', project: 'field76644842', lastName: 'field76644814', city: 'field76644864',
  team: 'field77481944', teamOther: 'field164881516', supervisor: 'field77482496', coordinator: 'field128039917', lswp: 'field104692223',
  damaged: 'field76659164', notes: 'field99454575', balanceDue: 'field95086187', amountDue: 'field95086222', paymentMethod: 'field76664334',
};
const CHECKLIST_PAYMENTS = ['Cash', 'Scanned Check at Install', 'Credit Card Collected at Install', 'ALLY No Form Needed', 'FCC No From Needed', 'GoodLeap No Form Needed', 'Greensky No Form Needed', 'Sunlight No Form Needed', 'Upgrade No Form Needed', 'Aqua', 'Dividend', 'Foundation', 'Power Pay', 'Medallion'];
// Logística: "Credit / Cash / Check" (texto livre) → opção do Formstack
const matchPayment = (text) => {
  const t = norm(text);
  if (!t) return null;
  if (/cash|dinheiro/.test(t)) return 'Cash';
  if (/check|cheque/.test(t)) return 'Scanned Check at Install';
  if (/credit|cart|card|cc\b/.test(t)) return 'Credit Card Collected at Install';
  return CHECKLIST_PAYMENTS.find((o) => t.includes(norm(o).split(' ')[0])) || null;
};
const parseAmount = (v) => {
  if (v === undefined || v === null || String(v).trim() === '') return null;
  const n = parseFloat(String(v).replace(/[^0-9.,-]/g, '').replace(/,(?=\d{3}(\D|$))/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const CHECKLIST_TEAMS = { PM1: 'PM Home Remodeling 1', PM2: 'PM 2', PM3: 'PM 3', PM4: 'PM 4', PM5: 'PM 5', PM6: 'PM 6', PM7: 'PM 7', PM8: 'PM 8', PM9: 'PM 9' };
const CHECKLIST_SUPERVISORS = ['Daniel Rice (Brooks)', 'Donald Steedman', 'Felipe Luna (Manny)', 'Herbert Steverson', 'Jonathan Jones', 'Mike Shipley', 'Will Loch', 'Guerdy Valcin'];
const CHECKLIST_COORDINATORS = ['Jenmi Gomez', 'Kristy Edwards', 'Vanessa Mojica'];
const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
// Texto livre do trabalho ("Brooks", "manny", "Will") → opção exata do select do Formstack
const matchOption = (text, options) => {
  const t = norm(text);
  if (!t) return null;
  return options.find((o) => norm(o) === t)
    || options.find((o) => norm(o).split(/[\s()]+/).filter((w) => w.length > 2).some((w) => t.includes(w)))
    || null;
};
export const completionChecklistUrl = (job) => {
  const p = new URLSearchParams();
  const set = (k, v) => { if (v !== undefined && v !== null && String(v).trim() !== '') p.set(CHECKLIST_FIELDS[k], String(v).trim()); };
  if (job?.job_type === 'INSTALL') set('workType', 'Install');
  if (job?.job_type === 'SERVICE') set('workType', 'Service');
  const order = job?.order_number && job.order_number !== 'None' ? job.order_number : (job?.service_order_number || '');
  set('project', order);
  const words = String(job?.customer_name || '').replace(/\b(jr|sr|ii|iii)\.?$/i, '').trim().split(/\s+/).filter(Boolean);
  if (words.length) set('lastName', words[words.length - 1]);
  set('city', job?.city);
  const team = job?.team || (job?.teams || [])[0];
  if (team) {
    if (CHECKLIST_TEAMS[team]) set('team', CHECKLIST_TEAMS[team]);
    else { set('team', 'Other/ Otro / Outro'); set('teamOther', team); }
  }
  set('supervisor', matchOption(job?.supervisor, CHECKLIST_SUPERVISORS));
  set('coordinator', matchOption(job?.project_coordinator, CHECKLIST_COORDINATORS));
  const year = parseInt(job?.year_built, 10);
  if (year > 1800) set('lswp', year < 1978 ? 'Yes' : 'No');
  set('damaged', 'No'); // padrão: sem dano (o instalador muda se precisar)
  set('notes', 'Job Complete');
  const amount = parseAmount(job?.amount_due); // logística: saldo a receber
  if (amount !== null) {
    set('balanceDue', amount > 0 ? 'Yes' : 'No');
    if (amount > 0) set('amountDue', amount.toFixed(2));
  }
  set('paymentMethod', matchPayment(job?.payment_type));
  const qs = p.toString();
  return qs ? `${EXTERNAL_LINKS.COMPLETION_CHECKLIST}?${qs}` : EXTERNAL_LINKS.COMPLETION_CHECKLIST;
};

// LEAD Form (LSWP Close Out). IDs do form "lswp_process_clean_copy". Matrizes: field<ID>-<linha>=<qualquer texto> = <coluna>
const LEAD_FIELDS = {
  customerName: 'field185997013', customerEmail: 'field185997014', address: 'field185997015', projectType: 'field185997016', activities: 'field185997018',
  allJobs: 'field185997141', interior: 'field186020751', exterior: 'field186020788',
  verifyName: 'field185997025', verifyAddress: 'field185997026', visual: 'field187591157', wetTest: 'field187591426', wetCloths: 'field185997031', dryCloths: 'field185997032',
  firm: 'field185997034', renovator: 'field187447366', renovatorEmail: 'field187463943', signName: 'field185997038',
};
const LEAD_ACTIVITIES = ['Post Warning Signs', 'Maintaining Containment', 'Post-Renovation cleaning of job site'];
const LEAD_FIRMS = { PM1: 'PM Home Remodeling 1', PM2: 'PM Home Remodeling 2', PM3: 'PM Home Remodeling 3', PM4: 'PM Home Remodeling 4', PM5: 'PM Home Remodeling 5', PM6: 'PM Home Remodeling 6', PM7: 'PM Home Remodeling 7', PM8: 'PM Home Remodeling 8', PM9: 'PM Home Remodeling 9' };
export const LEAD_RENOVATOR_EMAIL = 'pmhomeremodeling@gmail.com';
// "Name of Certified Renovator": opção = nº do certificado EPA; rótulo = nome (lista do Formstack)
const LEAD_RENOVATORS = {
  'Adam C Bell': '200246', 'Daniel Macedo De Sousa': '199934', 'Danyel J Da Silva Amorim': '200409', 'Esio Jose Da Mota Junior': '200400', 'Fabiano A Cardoso Cruz': '192375', 'Fabiano Cruz': '202982',
  'Fernanda P Defarias': '195825', 'Fernando Latorr': '197585', 'Fernando Oliveira Gonclaves': '203436', 'Filipe Machado': '202069', 'Gustavo C Lim': '202138', 'Gustavo Romao': '201462', 'Guilherme Caldas': '201823',
  'Guilherme Rodrigues': '202543', 'Joao Paulo': '9580-5531', 'Johnny Lucas Garbim': '197029', 'Luan Gustavo Dos Santos': '196038', 'Leonardo Lennen Casotti': '196040', 'Maderson Alves Santos': '197087', 'Mateus Ferreiri': '193094',
  'Matthew Corsiglia': '199150', 'Oleh Shvetsov': '196975', 'Michael Godinez Oregon': '201042', 'Paul Lazariuk': '197786', 'Rodrigo Goncalves Da Acosta': '197624', 'Roman Kozlyuk': '202210', 'Roman Maksymchuk': '203366',
  'Samuel J Dasilva Amorim': '193050', 'Sergio Luiz Correia': '196598', 'Thiago Jose Da Silva': '202029', 'Sergio R Santos': '197291', 'Thies Marquez Spineli': '199855', 'Victor Garcia Andrade': '203583',
};
// Nome cadastrado do líder ("Daniel Macedo") → certificado: mesmo primeiro nome e todas as outras palavras presentes no rótulo
export const matchLeadRenovator = (fullName) => {
  const words = norm(fullName).split(/\s+/).filter((w) => w.length > 1);
  if (words.length < 2) return null;
  const hit = Object.entries(LEAD_RENOVATORS).find(([label]) => {
    const lw = norm(label).split(/\s+/);
    return lw[0] === words[0] && words.every((w) => lw.includes(w));
  });
  return hit ? hit[1] : null;
};
const splitName = (full) => {
  const words = String(full || '').replace(/\b(jr|sr|ii|iii)\.?$/i, '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  return words.length === 1 ? { first: words[0], last: '' } : { first: words.slice(0, -1).join(' '), last: words[words.length - 1] };
};
export const leadFormUrl = (job, renovatorName) => {
  const p = new URLSearchParams();
  const set = (k, v) => { if (v !== undefined && v !== null && String(v).trim() !== '') p.set(k, String(v).trim()); };
  const F = LEAD_FIELDS;
  const name = splitName(job?.customer_name);
  const setName = (f) => { if (name) { set(`${f}-first`, name.first); set(`${f}-last`, name.last); } };
  const setAddress = (f) => { set(`${f}-address`, job?.address); set(`${f}-city`, job?.city); set(`${f}-state`, 'GA'); set(`${f}-zip`, job?.zip_code); };
  setName(F.customerName); set(F.customerEmail, job?.customer_email); setAddress(F.address);
  const windows = parseInt(job?.windows, 10) || 0;
  const doors = parseInt(job?.doors, 10) || 0;
  set(F.projectType, windows || !doors ? 'Window' : 'Door');
  set(F.activities, LEAD_ACTIVITIES.join('\n'));
  [[F.allJobs, 6], [F.interior, 7], [F.exterior, 3]].forEach(([f, rows]) => { for (let i = 1; i <= rows; i++) set(`${f}-${i}`, 'x = Yes'); });
  setName(F.verifyName); setAddress(F.verifyAddress); // "Copy Name from Checklist Above"
  set(`${F.visual}-1`, 'x = Select One'); // Visual inspection PASSED
  set(`${F.wetTest}-1`, 'x = Select One'); // ALL WET CLOTH TESTS PASSED
  const openings = windows + doors;
  if (openings > 0) { set(F.wetCloths, openings * 2); set(F.dryCloths, openings); }
  const team = job?.team || (job?.teams || [])[0];
  set(F.firm, LEAD_FIRMS[team]);
  set(F.renovator, matchLeadRenovator(renovatorName));
  set(F.renovatorEmail, LEAD_RENOVATOR_EMAIL);
  setName(F.signName);
  return `${EXTERNAL_LINKS.LEAD_FORM}?${p.toString()}`;
};
